/** "Hồ sơ tên miền": the dossier queue and its review panel (src/pages/work/Dossier*.tsx). */
export const dossiers = {
  title: 'Domain dossiers',
  subtitle: 'Every domain waiting for its dossier before it activates: what the customer still has to send, what the system checked on its own, what needs a person.',
  note: 'The system approves on its own only when everything matches (eKYC passed, or a digitally signed declaration passing every check). Everything else is here for a person to decide; nothing is rejected automatically, and an order under review is not cancelled at its deadline.',
  stats: {
    awaiting: 'Waiting for customer',
    review: 'Needs manual review',
    warning: 'Warnings',
    dueSoon: 'Deadline < 24 h',
    activatedToday: 'Activated today',
    checking: 'Checking automatically',
    activating: 'Approved, activating',
  },
  board: { label: 'View', list: 'List', board: 'By stage', unassigned: 'Unassigned' },
  stage: {
    AWAITING_CUSTOMER: 'Waiting for customer',
    AUTO_CHECKING: 'Checking automatically',
    NEEDS_REVIEW: 'Needs manual review',
    WARNING: 'Warning',
    ACTIVATING: 'Approved – activating',
    ACTIVATED: 'Activated',
  },
  method: {
    EKYC: 'eKYC',
    VNEID: 'VNeID',
    DECLARATION_SIGNED: 'Declaration, digitally signed',
    DECLARATION_SCAN: 'Declaration, scanned',
    DECLARATION: 'Declaration',
    DOCUMENTS: 'Documents',
    STAFF: 'Staff review',
    none: 'Nothing yet',
  },
  finding: {
    NOTHING_SUBMITTED: 'Nothing submitted yet',
    CORRECTION_REQUESTED: 'Sent back for correction',
    VERIFICATION_FAILED: 'Last verification did not pass',
    CHECKING: 'Being checked',
    NEEDS_STAFF: 'Needs staff',
    WARNING: 'Warning',
    AUTO_APPROVED: 'Approved automatically',
    STAFF_APPROVED: 'Approved by staff',
  },
  reason: {
    EKYC_MISMATCH: 'eKYC: the data or face does not match the ID document',
    EKYC_FAILED: 'eKYC failed (unusable photos, document not genuine, liveness)',
    VERIFICATION_INCONCLUSIVE: 'Verification not conclusive enough to approve automatically',
    DOSSIER_MANUAL_REVIEW: 'A policy requires a staff review',
    DECLARATION_REVIEW_SCAN: 'Scanned / hand-signed declaration: compare by eye',
    DECLARATION_REVIEW_SIGNED: 'Digitally signed declaration: a check did not pass',
    DECLARATION_REVIEW_MATCHES: 'Everything read matches: approve in one click',
    MANUAL_ACTIVATION: 'Needs manual activation',
    SUBJECT_CHANGED_AFTER_DOSSIER: 'The registrant changed after the dossier completed',
    VNNIC_SYNC_MISSING: 'Not sent to VNNIC yet',
    VNNIC_SYNC_STUCK: 'VNNIC sync stuck',
    VNNIC_SYNC_REJECTED: 'VNNIC rejected it or asked for a correction',
    VNNIC_SYNC_DRIFT: 'Dossier differs from what was sent to VNNIC',
    WORKFLOW_FAILED: 'Activation failed',
    WORKFLOW_MANUAL_REVIEW: 'Activation waiting for a person',
    changed: 'Changed: {{parts}}',
    failedChecks: 'Checks not passed: {{checks}}',
    syncError: '{{meaning}} ({{code}})',
    fields: '{{field}}: {{result}}',
  },
  code: {
    NAME_MISMATCH: 'Name does not match',
    BIRTH_MISMATCH: 'Date of birth does not match',
    NUMBER_MISMATCH: 'ID number does not match',
    FACE_MISMATCH: 'Face does not match the ID photo',
    NAME_UNREADABLE: 'Name unreadable',
    BIRTH_UNREADABLE: 'Date of birth unreadable',
    NUMBER_UNREADABLE: 'ID number unreadable',
    ID_UNREADABLE: 'ID document unreadable',
    NAME_NOT_DECLARED: 'Name not declared',
    BIRTH_NOT_DECLARED: 'Date of birth not declared',
    NUMBER_NOT_DECLARED: 'ID number not declared',
    DOCUMENT_NOT_GENUINE: 'ID document not genuine',
    DOCUMENT_NOT_ACCEPTED: 'ID document type not accepted',
    LIVENESS_FAILED: 'Liveness check failed',
    METHOD_NOT_ACCEPTED: 'Verified, but with a method the policy does not accept',
    VERSION_MISMATCH: 'Verified another version of the registrant',
  },
  changedPart: { legal_identity: 'legal identity', identifiers: 'identifiers', addresses: 'addresses', contacts: 'contacts' },
  subjectType: {
    INDIVIDUAL: 'Individual',
    ORGANIZATION: 'Organisation',
    HOUSEHOLD_BUSINESS: 'Household business',
    GOVERNMENT_ENTITY: 'Government body',
    FOREIGN_ENTITY: 'Foreign entity',
    OTHER: 'Other',
  },
  operation: {
    DOMAIN_REGISTRATION: 'Registration',
    DOMAIN_TRANSFER_IN: 'Transfer in',
    DOMAIN_REGISTRANT_CHANGE: 'Registrant change',
    DOMAIN_NAME_CHANGE: 'Name change / holder transfer',
    none: 'Active domain',
  },
  col: {
    domain: 'Domain',
    customer: 'Customer',
    registrant: 'Registrant',
    stage: 'Stage',
    method: 'Verified by',
    finding: 'What the system found',
    waiting: 'Waiting since / deadline',
    assignee: 'Assignee',
    action: 'Action',
  },
  deadline: {
    due: 'due {{when}}',
    overdue: 'overdue',
    paused: 'deadline paused while staff decide',
    none: 'no deadline',
  },
  filter: {
    stage: 'Stage',
    open: 'All open',
    method: 'Verified by',
    tld: 'TLD',
    tldPlaceholder: 'vn, com.vn…',
    assignee: 'Assignee',
    me: 'Mine',
    unassigned: 'Unassigned',
    dueSoon: 'Deadline < 24 h only',
    search: 'Domain',
    searchPlaceholder: 'Search a domain…',
    any: 'Any',
  },
  action: {
    remind: 'Remind customer',
    review: 'Review',
    open: 'Open',
    view: 'View',
    next: 'Next item',
    assignMe: 'Assign to me',
    assignOther: 'Assign to…',
    note: 'Note',
    approve: 'Approve',
    reject: 'Request correction',
  },
  empty: 'Nothing in this list.',
  emptyHint: 'No domain is waiting for its dossier with these filters.',
  page: '{{from}}–{{to}} of {{total}}',
  hstm: {
    slot: 'Extra file for the VNNIC dossier',
    slotHint: 'A document VNNIC HSTM requires that the platform has no form for (sensitive-keyword commitment, gov.vn minutes, name-change request, …). A PDF\'s digital signature is checked automatically.',
    reason: 'Reason (audited)',
    sensitiveKeyword: 'The name contains a sensitive keyword',
  },
  remind: {
    title: 'Remind the customer',
    description: 'E-mails the registrant (or the account holder when the registrant has no e-mail) that the dossier of {{domain}} is still missing, with the deadline and how to complete it (eKYC or a declaration). At most once every 24 hours per dossier; recorded and audited.',
    note: 'Note for the customer (optional)',
    noteHint: 'Shown in the e-mail, e.g. what is still missing.',
    submit: 'Send reminder',
    sent: 'Reminder sent to the registrant.',
    tooSoon: 'A reminder was sent less than 24 hours ago. Next one possible {{when}}.',
    lastSent: 'Last reminder {{when}} ({{count}} sent)',
  },
  panel: {
    title: 'Dossier review',
    loadError: 'Could not load the dossier',
    order: 'Order',
    operation: 'Operation',
    dossier: 'Dossier',
    registrant: 'Registrant (subject version {{version}})',
    newerVersion: 'The subject has a newer version than the one this dossier pins.',
    subjectType: 'Type',
    identifiers: 'Identifiers (masked)',
    emails: 'E-mail',
    verifications: 'Verifications',
    noVerifications: 'No verification of this subject version yet.',
    pinned: 'used by the dossier',
    declarations: 'Declarations (bản khai)',
    checks: 'Checks',
    documents: 'Documents',
    documentsHint: 'Documents open on their page; each read asks for a reason and is audited.',
    history: 'Dossier history',
    items: 'Work items',
    reminders: 'Reminders',
    noReminders: 'No reminder sent.',
    finding: 'What the system found',
    decisionHint: 'Approve records the decision through the review item (a staff verification, or the declaration accepted): the waiting workflow completes the dossier and the domain activates. Request correction sends the dossier back and e-mails the registrant.',
    noItem: 'No review item: nothing to decide here.',
    requestItem: 'This is decided on its request page.',
    openItem: 'Open the work item',
    openDomain: 'Open the domain',
    openSubject: 'Open the subject',
    openOrder: 'Open the order',
    keyboard: 'Keys: J / K next / previous row, Enter review, A approve, R request correction, N next item after a decision.',
    decided: 'Decision recorded. Moving to the next item.',
    lastItem: 'Decision recorded. No more items in this list.',
    noteTitle: 'Add a note',
    noteHint: 'Internal, never sent to the customer; audited (without the text).',
  },
  ops: {
    chip: 'Operations',
    hidden: '{{count}} operations items (registry, reconciliation, e-mail, webhook, workflow) are hidden here.',
    show: 'Show them',
    openHub: 'Open the Operations hub',
    showing: 'Showing operations items only.',
    back: 'Back to dossier work',
  },
  badge: '{{count}} dossiers need manual review',
};
